import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography, keyframes } from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PlanSelectionDialog from './PlanSelectionDialog';

const SELF_SERVE_TIERS = new Set(['basic', 'pro', 'elite']);

const floatIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const haloPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50%      { transform: scale(1.12); opacity: 0.85; }
`;

/**
 * Premium read-only paywall shown over the (blurred) content when a tenant's
 * time-bound trial has ended. The data underneath stays rendered + blurred — a
 * deliberate "there's value here, subscribe to read it" teaser — while this card
 * captures all interaction. The CTA opens the shared PlanSelectionDialog so the
 * user picks a tier + monthly/yearly and goes straight to Stripe Checkout.
 *
 * Mounted by DashboardLayout, which owns the blur on the content behind it.
 */
export default function TrialExpiredOverlay({ tier }: { tier: string }) {
  const { t } = useTranslation();
  const [planOpen, setPlanOpen] = useState(false);
  const canCheckout = SELF_SERVE_TIERS.has(tier);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: (t) => t.zIndex.appBar - 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        // Soft scrim so the card reads cleanly over whatever data sits behind.
        background:
          'radial-gradient(120% 120% at 50% 0%, rgba(124,58,237,0.10) 0%, rgba(15,15,25,0.28) 55%, rgba(15,15,25,0.40) 100%)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 440,
          textAlign: 'center',
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(124,58,237,0.18)',
          boxShadow:
            '0 24px 64px -16px rgba(76,29,149,0.35), 0 2px 8px rgba(15,15,25,0.08)',
          animation: `${floatIn} 420ms cubic-bezier(0.16, 1, 0.3, 1) both`,
          overflow: 'hidden',
        }}
      >
        {/* Top accent hairline */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'linear-gradient(90deg, #A78BFA 0%, #7C3AED 50%, #6D28D9 100%)',
          }}
        />

        {/* Lock medallion with pulsing halo */}
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2.5 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: -10,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(124,58,237,0.35) 0%, rgba(124,58,237,0) 70%)',
              animation: `${haloPulse} 3.2s ease-in-out infinite`,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
              boxShadow: '0 8px 24px -6px rgba(124,58,237,0.6)',
            }}
          >
            <LockRoundedIcon sx={{ color: '#fff', fontSize: 30 }} />
          </Box>
        </Box>

        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ letterSpacing: '-0.02em', mb: 1 }}
        >
          {t('trialExpired.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3.5, lineHeight: 1.6 }}
        >
          {t('trialExpired.body')}
        </Typography>

        {canCheckout ? (
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<AutoAwesomeRoundedIcon />}
            onClick={() => setPlanOpen(true)}
            sx={{
              py: 1.25,
              fontWeight: 700,
              fontSize: '0.95rem',
              borderRadius: 2.5,
              textTransform: 'none',
              boxShadow: '0 10px 28px -8px rgba(124,58,237,0.7)',
            }}
          >
            {t('trialExpired.startSubscription')}
          </Button>
        ) : (
          <Stack
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: 2.5,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t('trialExpired.contactUs')}
            </Typography>
          </Stack>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 2 }}
        >
          {t('trialExpired.cancelAnytime')}
        </Typography>
      </Box>

      {canCheckout && (
        <PlanSelectionDialog
          open={planOpen}
          onClose={() => setPlanOpen(false)}
          currentTier={tier}
          subscribed={false}
        />
      )}
    </Box>
  );
}
